import { useEffect, useRef, useState } from "react";
import { ChevronDown, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { createAnnouncement, updateAnnouncement } from "@/lib/api";
import { useAnnouncements } from "@/lib/AnnouncementsContext";
import { AnnouncementColorPicker, AnnouncementColorSwatch } from "./AnnouncementColorPicker";

export type AnnouncementPickerProps = {
  // Current announcement id, or null for "no announcement". Ignored (shows
  // the placeholder instead) when `placeholder` is set - used by the bulk
  // toolbar, where there's no single "current" value across the selection.
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  noneLabel?: string;
  disabled?: boolean;
  className?: string;
};

// Shared "assign an announcement" control for the product form and the
// products bulk-select toolbar. A custom dropdown (not a native <select>,
// which can't render per-option color swatches) lists existing
// announcements with a preview of their colors, plus a "New announcement..."
// row that reveals a tiny inline create form - full management
// (rename/delete) lives in AnnouncementsModal instead.
export function AnnouncementPicker({
  value,
  onChange,
  placeholder,
  noneLabel,
  disabled,
  className,
}: AnnouncementPickerProps) {
  const { t } = useTranslation();
  const { announcements, loading, upsert } = useAnnouncements();
  const [isOpen, setIsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [newMessageEn, setNewMessageEn] = useState("");
  const [newBackgroundColor, setNewBackgroundColor] = useState<string | null>(null);
  const [newTextColor, setNewTextColor] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [colorSaving, setColorSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (containerRef.current?.contains(target)) return;
      // The color popover is portaled to <body>, so it's outside
      // containerRef in the DOM even while open from inside this picker -
      // don't treat clicks in it as an outside click.
      if (target.closest?.("[data-announcement-color-popover]")) return;
      setIsOpen(false);
      setCreating(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setCreating(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const select = (id: string | null) => {
    onChange(id);
    setIsOpen(false);
  };

  const startCreate = () => {
    setCreating(true);
    setCreateError(null);
  };

  const cancelCreate = () => {
    setCreating(false);
    setNewMessage("");
    setNewMessageEn("");
    setNewBackgroundColor(null);
    setNewTextColor(null);
    setCreateError(null);
  };

  const saveNew = async () => {
    const trimmed = newMessage.trim();
    if (!trimmed) {
      setCreateError(t("common.required"));
      return;
    }

    setSaving(true);
    setCreateError(null);
    try {
      const created = await createAnnouncement(trimmed, newMessageEn.trim() || null, {
        backgroundColor: newBackgroundColor,
        textColor: newTextColor,
      });
      upsert(created);
      cancelCreate();
      select(created.id);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : t("announcements.couldNotAdd"));
    } finally {
      setSaving(false);
    }
  };

  const selected = value ? announcements.find(a => a.id === value) : undefined;

  // Colors live on the shared Announcement record, not the product, so
  // adjusting them here edits that announcement for every product using it
  // - same as doing it from AnnouncementsModal, just reachable inline next
  // to the picker instead of a separate trip through the nav menu.
  const changeSelectedColors = async (colors: { backgroundColor: string | null; textColor: string | null }) => {
    if (!selected) return;
    setColorSaving(true);
    try {
      const updated = await updateAnnouncement(selected.id, selected.message, selected.message_en, colors);
      upsert(updated);
    } catch {
      // Swallow - this is a convenience shortcut; full editing with error
      // feedback still lives in AnnouncementsModal.
    } finally {
      setColorSaving(false);
    }
  };

  const triggerLabel = placeholder && value === null ? placeholder : selected ? selected.message : noneLabel ?? t("announcements.none");

  return (
    <div className="flex items-center gap-2">
      <div ref={containerRef} className="relative flex-1 min-w-0">
        <button
          type="button"
          disabled={disabled || loading}
          onClick={() => setIsOpen(prev => !prev)}
          className={`${className ?? "input w-full bg-app-control border-app-border text-foreground"} flex items-center gap-2 text-left disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
        >
          <AnnouncementColorSwatch
            backgroundColor={selected?.background_color ?? null}
            textColor={selected?.text_color ?? null}
            size={18}
          />
          <span className={`flex-1 min-w-0 truncate ${!selected && placeholder && value === null ? "text-app-muted" : ""}`}>
            {triggerLabel}
          </span>
          <ChevronDown size={16} className={`shrink-0 text-app-muted transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
        </button>

        <div
          className={`absolute z-50 top-full mt-1 left-0 w-full min-w-56 bg-app-surface text-foreground rounded-xl shadow-lg ring-1 ring-app-border overflow-hidden transition-all duration-150 ease-in-out ${
            isOpen ? "opacity-100 translate-y-0 scale-100" : "opacity-0 -translate-y-1 scale-95 pointer-events-none"
          }`}
        >
          {creating ? (
            <div className="flex flex-col gap-2 p-3">
              <input
                autoFocus
                type="text"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                placeholder={t("announcements.messagePlaceholder")}
                className="input input-sm w-full bg-app-control border-app-border text-foreground"
              />
              <input
                type="text"
                value={newMessageEn}
                onChange={e => setNewMessageEn(e.target.value)}
                placeholder={t("announcements.messageEnPlaceholder")}
                className="input input-sm w-full bg-app-control border-app-border text-foreground"
              />
              <AnnouncementColorPicker
                backgroundColor={newBackgroundColor}
                textColor={newTextColor}
                onChange={({ backgroundColor, textColor }) => {
                  setNewBackgroundColor(backgroundColor);
                  setNewTextColor(textColor);
                }}
              />
              {createError && <span className="text-xs text-red-500">{createError}</span>}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={saveNew}
                  disabled={saving}
                  className="btn btn-sm border-none bg-brand text-white hover:bg-brand/90 disabled:opacity-50"
                >
                  {t("common.save")}
                </button>
                <button
                  type="button"
                  onClick={cancelCreate}
                  disabled={saving}
                  className="btn btn-sm border-none bg-transparent text-app-muted hover:bg-app-surface-hover disabled:opacity-50"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          ) : (
            <ul className="max-h-64 overflow-y-auto py-1">
              <li>
                <button
                  type="button"
                  onClick={() => select(null)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-app-surface-hover transition-colors duration-150 cursor-pointer ${
                    value === null ? "text-brand font-medium" : "text-foreground"
                  }`}
                >
                  {noneLabel ?? t("announcements.none")}
                </button>
              </li>
              {announcements.map(a => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => select(a.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-app-surface-hover transition-colors duration-150 cursor-pointer ${
                      value === a.id ? "text-brand font-medium" : "text-foreground"
                    }`}
                  >
                    <AnnouncementColorSwatch backgroundColor={a.background_color} textColor={a.text_color} size={18} />
                    <span className="flex-1 min-w-0 truncate">{a.message}</span>
                  </button>
                </li>
              ))}
              <li className="border-t border-app-border">
                <button
                  type="button"
                  onClick={startCreate}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-brand hover:bg-brand/10 transition-colors duration-150 cursor-pointer"
                >
                  <Plus size={14} />
                  {t("announcements.createNew")}
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>

      {selected && (
        <AnnouncementColorPicker
          backgroundColor={selected.background_color}
          textColor={selected.text_color}
          onChange={changeSelectedColors}
          disabled={colorSaving}
        />
      )}
    </div>
  );
}
